/**
 * Job Category Prefill Text API Contract
 *
 * ts-rest contract definition for the job category prefill text endpoint.
 *
 * Endpoint  : GET /jobs/job-category-prefill-text
 * Method    : GET
 * Auth      : Required (standard bearer token via middleware)
 * Response  : Array of job category prefill text entries ordered by name
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Zod schema for a single job category prefill text record
 */
export const jobCategoryPrefillTextSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string(),
  prefill_text: z.string(),
  job_category_id: z.number().nullable(),
  created_time: z.string(),
});

/**
 * Zod schema for the job category prefill text list response
 */
export const jobCategoryPrefillTextsResponseSchema = z.array(jobCategoryPrefillTextSchema);

/**
 * TypeScript types inferred from Zod schemas
 */
export type JobCategoryPrefillText = z.infer<typeof jobCategoryPrefillTextSchema>;
export type JobCategoryPrefillTextsResponse = z.infer<typeof jobCategoryPrefillTextsResponseSchema>;

/**
 * Job Category Prefill Text API Contract
 *
 * Base path: /jobs/job-category-prefill-text  (mounted in apps/backend/src/domains/works-lite/jobs/routes.ts)
 */
export const jobCategoryPrefillTextContract = c.router({
  /**
   * Get all job category prefill text entries
   *
   * GET /jobs/job-category-prefill-text
   *
   * Returns all rows from job_category_prefill_text, sorted alphabetically by name.
   */
  getJobCategoryPrefillTexts: {
    method: 'GET',
    path: '/',
    responses: {
      200: jobCategoryPrefillTextsResponseSchema,
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Get all job category prefill text entries ordered by name',
    description: 'Returns all job category prefill text rows ordered alphabetically by name.',
  },
});
